'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Sparkles,
  FileText,
  Settings,
  Mic2,
  Calendar,
  BarChart3,
  Repeat,
  type LucideIcon,
} from 'lucide-react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

interface NavItem {
  icon: LucideIcon;
  href: string;
  label: string;
  matchPaths?: string[];
  featureFlag?: string;
}

// Posts is the dashboard home. Overview/Audience pages remain reachable
// by direct URL (Audience by /dashboard/audience; Overview redirects to
// Posts) but are no longer surfaced in the nav.
const navItems: NavItem[] = [
  {
    icon: FileText,
    href: '/dashboard/posts',
    label: 'Posts',
    matchPaths: ['/dashboard/posts'],
  },
  {
    icon: Sparkles,
    href: '/dashboard/moments',
    label: 'Moments',
    matchPaths: ['/dashboard/moments'],
  },
  // Sales sits between Moments and Speakers per the Sales Cockpit plan.
  // Surfaces deal-grouped search, playbooks, and the pre-call brief.
  {
    icon: BarChart3,
    href: '/dashboard/sales',
    label: 'Sales',
    matchPaths: ['/dashboard/sales'],
  },
  {
    icon: Mic2,
    href: '/dashboard/speakers',
    label: 'Speakers',
    matchPaths: ['/dashboard/speakers'],
  },
  {
    icon: Calendar,
    href: '/dashboard/bookings',
    label: 'Bookings',
    matchPaths: ['/dashboard/bookings'],
    featureFlag: 'bookings_enabled',
  },
  {
    icon: Repeat,
    href: '/dashboard/routines',
    label: 'Routines',
    matchPaths: ['/dashboard/routines'],
  },
  {
    icon: Settings,
    href: '/dashboard/settings',
    label: 'Settings',
    matchPaths: ['/dashboard/settings'],
  },
];

interface SidebarNavProps {
  isCollapsed: boolean;
}

export function SidebarNav({ isCollapsed }: SidebarNavProps) {
  const pathname = usePathname();
  const bookingsEnabled = useFeatureIsOn('bookings_enabled');

  const featureFlags: Record<string, boolean> = {
    bookings_enabled: bookingsEnabled,
  };

  const isActive = (item: NavItem) => {
    return item.matchPaths?.some((path) => pathname?.startsWith(path)) ?? false;
  };

  const visibleItems = navItems.filter(item =>
    !item.featureFlag || featureFlags[item.featureFlag]
  );

  return (
    <nav className="flex flex-col gap-1">
      {visibleItems.map((item) => {
        const active = isActive(item);
        const Icon = item.icon;

        const button = (
          <Button
            key={item.href}
            variant="ghost"
            asChild
            className={cn(
              'w-full justify-start gap-3 h-10',
              isCollapsed && 'justify-center px-0',
              active && 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
              !active && 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Link href={item.href}>
              <Icon className={cn('h-5 w-5 shrink-0', active && 'text-primary')} />
              {!isCollapsed && (
                <span className={cn('font-medium', active && 'text-primary')}>
                  {item.label}
                </span>
              )}
            </Link>
          </Button>
        );

        if (isCollapsed) {
          return (
            <Tooltip key={item.href}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent side="right">
                <p>{item.label}</p>
              </TooltipContent>
            </Tooltip>
          );
        }

        return button;
      })}
    </nav>
  );
}
